import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button, Title, Divider, LoadingOverlay, Box } from "@mantine/core";
import { shallow } from "zustand/shallow";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import ViewError from "@components/Microsite/ViewError";
import ViewLoader from "@components/Microsite/ViewLoader";
import LocationLocator from "@components/Microsite/LocationLocator";
import UserLocation from "@components/Microsite/UserLocation";
import EffortView from "./EffortView";
import MembershipView from "./MembershipView";
// import UserCheck from "./UserCheck";
import UserCheck from "@components/Microsite/UserCheck";
import VehicleView from "./VehicleView";
import VehicleVerificationView from "./VehicleVerificationView";
import RideChallengeDashboard from "./RideChallengeDashboard";
import Wrapper from "./RideChallengeWrapper";
import PresentAtLocationInstructions from "./PresentAtLocationInstructions";

import useAppStore from "@components/Microsite/microsite-store";
import { getGeolocation } from "@components/Microsite/geoInterceptor";

import { getPlacementImage } from "@components/Asset/helpers";
import { placementDefs } from "@components/Asset/placementDefs";
import { rideChallengeUserLookup } from "./userLookup";
import sendActionEvents, {
  actionIds,
} from "@components/EntityActionAssociator/sendActionEvents";

export default function RideChallengeApp({
  appConfigData,
  campaignId,
  entityInfoData,
  preview,
}) {
  const [error, setError] = useState(null);
  const [locating, setLocating] = useState(true);

  const { t, i18n } = useTranslation();

  const {
    activeStep,
    appConfig,
    colors,
    coords,
    coordsNoLocation,
    email,
    entityInfo,
    appLoading,
    location,
    phone,
    setAppConfig,
    setCampaignId,
    setEntityInfo,
    setColors,
    setCoords,
    setEmail,
    setLocation,
    user,
    zip,
  } = useAppStore(
    (state) => ({
      activeStep: state.activeStep,
      appConfig: state.appConfig,
      appLoading: state.appLoading,
      campaignId: state.campaignId,
      coords: state.coords,
      coordsNoLocation: state.coordsNoLocation,
      email: state.email,
      entityInfo: state.entityInfo,
      location: state.location,
      phone: state.phone,
      setAppConfig: state.setAppConfig,
      setCampaignId: state.setCampaignId,
      setEntityInfo: state.setEntityInfo,
      setColors: state.setColors,
      setCoords: state.setCoords,
      setEmail: state.setEmail,
      setLocation: state.setLocation,
      user: state.user,
      zip: state.zip,
    }),
    shallow
  );

  useEffect(() => {
    trackPageView();
  }, []);

  // Set up axios interceptor to fetch geolocation before API calls
  useEffect(() => {
    const interceptorId = axios.interceptors.request.use(
      async (config) => {
        // Only add geolocation to ride-challenge and microsite API calls
        if (
          config.url &&
          (config.url.indexOf("ride-challenge") > -1 ||
            config.url.indexOf("microsite") > -1)
        ) {
          try {
            const coords = await getGeolocation();
            if (coords) {
              // Add coordinates to request data
              if (config.method === "post" || config.method === "put") {
                if (config.data) {
                  // If data is FormData, append to it
                  if (config.data instanceof FormData) {
                    config.data.append("latitude", coords.latitude);
                    config.data.append("longitude", coords.longitude);
                  } else if (typeof config.data === "object") {
                    // If data is an object, merge coordinates
                    config.data = {
                      ...config.data,
                      latitude: coords.latitude,
                      longitude: coords.longitude,
                    };
                  }
                } else {
                  // If no data exists, create new object
                  config.data = {
                    latitude: coords.latitude,
                    longitude: coords.longitude,
                  };
                }
              } else if (config.method === "get") {
                // For GET requests, add as query params
                config.params = {
                  ...config.params,
                  latitude: coords.latitude,
                  longitude: coords.longitude,
                };
              }
            }
          } catch (error) {
            // Silently fail - don't block the request if geolocation fails
            console.warn("Geolocation fetch failed:", error);
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.request.eject(interceptorId);
    };
  }, []);

  useEffect(() => {
    document.body.style.backgroundColor = "var(--mantine-color-dark-9)";
    setEntityInfo(entityInfoData);
    setCampaignId(campaignId);
    setAppConfig(appConfigData);

    if (
      entityInfoData &&
      entityInfoData.language
      // entityInfoData.language !== "en"
    ) {
      i18n.changeLanguage(entityInfoData.language);
    } else {
      i18n.changeLanguage("en");
    }

    if (preview) {
      setEmail("example@example.com");
    }
  }, []);

  useEffect(() => {
    if (!appConfig) return;
    if (user) {
      rideChallengeUserLookup({
        userInfo: {
          email: email,
          mobile_phone: phone,
        },
        onModal: (user) => {
          true;
        },
      }).then((user) => {
        setLocating(false);
      });
      return;
    }
    fetchGeolocation();
  }, [JSON.stringify(appConfig)]);

  useEffect(() => {
    if (!appConfig) return;
    const backgroundImage = getPlacementImage(
      appConfig,
      entityInfo.assets,
      placementDefs.background
    );

    if (backgroundImage) {
      document.body.style.background = `url(${backgroundImage}) no-repeat center center`;
      document.body.style.backgroundSize = "cover";
      document.body.style.backgroundAttachment = "fixed";
    } else {
      document.body.style.background = "var(--mantine-color-dark-9)";
      document.body.style.backgroundSize = "initial";
      document.body.style.backgroundAttachment = "initial";
    }

    if (appConfig.colors) {
      setColors({ ...colors, ...appConfig.colors });

      document.body.style.backgroundColor =
        appConfig.colors.background || "var(--mantine-color-dark-9)";
    }
  }, [JSON.stringify(entityInfo)]);

  function checkAndSetLocationFromResponse() {
    if (!entityInfoData.location_locked && entityInfoData.location) {
      setLocation(entityInfoData.location);
    }
    setLocating(false);
  }

  function trackPageView() {
    sendActionEvents({
      events: entityInfo?.paid_media_settings?.events || [],
      action_id: actionIds.PAGE_VIEW,
    });
  }

  function fetchGeolocation() {
    setLocating(true);
    const successCallback = (position) => {
      const lat = position.coords.latitude;
      const lng = position.coords.longitude;
      setCoords({
        latitude: lat,
        longitude: lng,
      });
      fetchLocationsFromGeo({
        latitude: lat,
        longitude: lng,
      });
    };

    const errorCallback = (error) => {
      // if location required, set error
      if (entityInfoData.location_locked) {
        setLocating(false);
        return setError(t("microsite:location_services_required_error"));
      } else {
        checkAndSetLocationFromResponse();
        setLocating(false);
        setCoords(null);
      }
    };

    navigator.geolocation.getCurrentPosition(successCallback, errorCallback, {
      enableHighAccuracy: false,
      timeout: 5000,
      maximumAge: Infinity,
    });
  }

  function fetchLocationsFromGeo(position) {
    if (!position) {
      if (entityInfoData.location_locked) {
        setError(t("microsite:no_coords_error"));
        setLocating(false);
      } else {
        checkAndSetLocationFromResponse();
      }
      return;
    }

    const req = {
      campaign_id: campaignId,
      campaign_effort_id: entityInfo.campaign_effort_id,
      latitude: position.latitude,
      longitude: position.longitude,
    };

    // if (entityInfo.location) req.location_id = entityInfo.location.id;

    axios
      .post(`/microsite/location-point-check/`, req)
      .then(({ data }) => {
        if (!data.response.length) {
          if (entityInfoData.location_locked) {
            setLocating(false);
            setError(t("microsite:not_at_location_error"));
          } else {
            checkAndSetLocationFromResponse();
          }
        } else {
          const foundGeospot = data.response.find((f) => f.geo_spot_id);
          const foundLocation = data.response.find(
            (f) => f.class_name === "Location"
          );

          if (foundLocation) {
            if (
              entityInfoData.location &&
              entityInfoData.location_locked &&
              entityInfoData.location.id !== foundLocation.id
            ) {
              setLocating(false);
              setError(t("microsite:not_at_location_error"));
            } else {
              setLocation({
                ...foundLocation,
                present_at_location: true,
              });
              setLocating(false);
            }
          } else {
            setLocation(foundGeospot);
            setLocating(false);
            // no location
            // if (entityInfoData.location_locked) {
            //   setLocating(false);
            //   setError(t("not_at_location_error"));
            // } else {
            //   if (foundGeospot) {
            //     setLocation(foundGeospot);
            //     setLocating(false);
            //   }
            // }
          }
        }
      })
      .catch((err) => {
        setError(err);
        setLocating(false);
      });
  }

  if (!entityInfo) return null;

  if (locating) {
    return (
      <Wrapper>
        <ViewLoader />
      </Wrapper>
    );
  }

  // return (
  //   <Wrapper>
  //     <VehicleView />
  //   </Wrapper>
  // );

  if (error) {
    return (
      <Wrapper>
        <ViewError
          title={t("microsite:generic_error_title")}
          order={1}
          subtitle={error}
          marginBottom={0}
        />
      </Wrapper>
    );
  }

  if (!entityInfoData.location_id && !location) {
    if ((zip || coords) && !coordsNoLocation) {
      return (
        <Wrapper>
          <LocationLocator buttonText={t("microsite:register_here_button")} />
        </Wrapper>
      );
    }

    return (
      <Wrapper>
        <UserLocation />
      </Wrapper>
    );
  }

  if (!email && !phone && !user) {
    return (
      <Wrapper>
        <UserCheck />
      </Wrapper>
    );
  }

  if (entityInfo.campaign_effort_id) {
    if (!location) {
      return (
        <Wrapper>
          <UserLocation />
        </Wrapper>
      );
    }

    if (preview) {
      return (
        <Wrapper>
          <EffortView
            onEnterSuccess={(usr) => true}
            onMoreEfforts={() => true}
            preview
          />
        </Wrapper>
      );
    }

    if (activeStep === "membership") {
      return (
        <Wrapper>
          <MembershipView />
        </Wrapper>
      );
    }

    if (activeStep === "vehicle") {
      return (
        <Wrapper>
          <VehicleView />
        </Wrapper>
      );
    }

    if (activeStep === "vehicle_verification") {
      return (
        <Wrapper>
          <VehicleVerificationView />
        </Wrapper>
      );
    }

    if (activeStep === "dashboard") {
      return (
        <div style={{ position: "relative" }}>
          <StyledOverlay>
            <LoadingOverlay
              visible={appLoading}
              style={
                {
                  // borderRadius: "10px",
                }
              }
            />
            {user && <RideChallengeDashboard />}
          </StyledOverlay>
        </div>
      );
    }

    if (activeStep === "present_at_location") {
      return (
        <Wrapper>
          <PresentAtLocationInstructions />
        </Wrapper>
      );
    }

    return (
      <Wrapper>
        <EffortView />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {((!coords && !zip && !location) || coordsNoLocation) && (
        <React.Fragment>
          <UserLocation />
        </React.Fragment>
      )}
      {(zip || coords) &&
        !coordsNoLocation &&
        !location &&
        !entityInfo.campaign_effort_id && (
          <LocationLocator buttonText={t("microsite:register_here_button")} />
        )}
    </Wrapper>
  );
}

const StyledOverlay = styled.div`
  .mantine-LoadingOverlay-overlay {
    background: rgba(0, 0, 0, 0.6) !important;
  }
`;
